"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

interface TestimonialProps {
  businessName: string;
  text: string;
  author: AuthorProps;
}

interface AuthorProps {
  authorName: string;
  title: string;
}

export function Testimonial({
  testimonials = [],
}: {
  testimonials: TestimonialProps[];
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  const goNext = () => setActiveIndex((i) => (i + 1) % testimonials.length);
  const goPrev = () =>
    setActiveIndex((i) => (i - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    const timer = setInterval(goNext, 6000);
    return () => clearInterval(timer);
  }, []);

  const current = testimonials[activeIndex];

  return (
    <div className="w-full overflow-hidden">
      <div className="relative flex ">
        {/* Center — main content */}
        <div className="flex-1 ">
          {/* Company badge */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`badge-${activeIndex}`}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16 }}
              transition={{ duration: 0.35 }}
              className="mb-8"
            >
              <p className="text-xs text-neutral-500">
                {current?.businessName}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Quote */}
          <div className="relative mb-8 min-h-[90px] md:mb-12 md:min-h-[120px]">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={`quote-${activeIndex}`}
                className="text-2xl md:text-4xl leading-[1.2] tracking-tight"
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {current?.text?.split(" ").map((word, i) => (
                  <motion.span
                    key={i}
                    className="inline-block mr-[0.28em]"
                    variants={{
                      hidden: { opacity: 0, y: 18 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.45,
                          delay: i * 0.04,
                          ease: [0.22, 1, 0.36, 1],
                        },
                      },
                      exit: {
                        opacity: 0,
                        y: -8,
                        transition: { duration: 0.18, delay: i * 0.015 },
                      },
                    }}
                  >
                    {word}
                  </motion.span>
                ))}
              </motion.blockquote>
            </AnimatePresence>
          </div>

          {/* Author + nav */}
          <div className="flex items-end justify-between">
            <AnimatePresence mode="wait">
              <motion.div
                key={`author-${activeIndex}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35, delay: 0.15 }}
                className="flex items-center gap-4"
              >
                <div>
                  <p className="text-sm font-medium">
                    {current?.author?.authorName}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {current?.author?.title}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center gap-3">
              {[
                { fn: goPrev, d: "M10 12L6 8L10 4" },
                { fn: goNext, d: "M6 4L10 8L6 12" },
              ].map(({ fn, d }, i) => (
                <motion.button
                  key={i}
                  onClick={fn}
                  className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-neutral-100 transition-colors"
                  whileTap={{ scale: 0.93 }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d={d}
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Dot indicators */}
        <div className="absolute -bottom-8 left-16 flex gap-2">
          {testimonials.map((_, i) => (
            <button key={i} onClick={() => setActiveIndex(i)}>
              <motion.div
                className="rounded-full bg-neutral-900"
                animate={{ width: i === activeIndex ? 20 : 6, height: 6 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
